import { getCurrentScope, addBreadcrumb } from '@sentry/core';
import type { Integration } from '@sentry/core';

import { getSystemInfo, sdk } from '../crossPlatform';

/**
 * System information integration.
 *
 * @deprecated 默认不启用，且其 system/device/network context 已被 `HttpContext` 与
 * `MiniappClient._prepareEvent` 覆盖。保留导出仅为向后兼容，将在 2.0 移除。请勿在新代码中使用。
 */
export class System implements Integration {
  /**
   * @inheritDoc
   */
  public static id: string = 'System';

  /**
   * @inheritDoc
   */
  public name: string = System.id;

  /**
   * @inheritDoc
   */
  public setupOnce(): void {
    this._addSystemContext();
    this._addNetworkContext();
    this._addLocationContext();
    this._addStorageContext();
    this._checkAppUpdate();
  }

  /**
   * Add system information to context
   */
  private _addSystemContext(): void {
    try {
      const systemInfo = getSystemInfo();
      if (systemInfo) {
        const scope = getCurrentScope();

        // Set device context
        scope.setContext('device', {
          name: systemInfo.model,
          model: systemInfo.model,
          brand: systemInfo.brand,
          family: systemInfo.platform,
          arch: systemInfo.platform,
        });

        // Set OS context
        scope.setContext('os', {
          name: systemInfo.system?.split(' ')[0] || 'unknown',
          version: systemInfo.system?.split(' ')[1] || 'unknown',
          kernel_version: systemInfo.version,
        });

        // Set app context
        scope.setContext('app', {
          app_name: (systemInfo as any).appName || 'unknown',
          app_version: systemInfo.version,
        });

        // Set screen context
        scope.setContext('screen', {
          screen_width: systemInfo.screenWidth,
          screen_height: systemInfo.screenHeight,
          screen_density: systemInfo.pixelRatio,
        });

        // Set tags
        scope.setTag('device.model', systemInfo.model);
        scope.setTag('device.brand', systemInfo.brand);
        scope.setTag('os.name', systemInfo.system?.split(' ')[0] || 'unknown');
        scope.setTag('os.version', systemInfo.system?.split(' ')[1] || 'unknown');
        scope.setTag('app.version', systemInfo.version);
        scope.setTag('language', systemInfo.language);
      }
    } catch (_e) {
      // Ignore errors when getting system info
    }
  }

  /**
   * Add network information to context
   */
  private _addNetworkContext(): void {
    try {
      if ((sdk() as any).getNetworkType) {
        (sdk() as any).getNetworkType({
          success: (res: { networkType: string; isConnected?: boolean }) => {
            const scope = getCurrentScope();
            scope.setContext('network', {
              type: res.networkType,
              connected: res.isConnected !== false,
            });
            scope.setTag('network.type', res.networkType);
          },
          fail: () => {
            // Ignore network type fetch errors
          },
        });
      }
    } catch (_e) {
      // Ignore errors when getting network info
    }
  }

  /**
   * Add location information to context (if available)
   */
  private _addLocationContext(): void {
    try {
      if ((sdk() as any).getLocation) {
        (sdk() as any).getLocation({
          type: 'gcj02',
          success: (res: { latitude: number; longitude: number; accuracy: number }) => {
            const scope = getCurrentScope();
            scope.setContext('location', {
              latitude: res.latitude,
              longitude: res.longitude,
              accuracy: res.accuracy,
            });
          },
          fail: () => {
            // Ignore location fetch errors (user might not grant permission)
          },
        });
      }
    } catch (_e) {
      // Ignore errors when getting location info
    }
  }
  /**
   * 采集存储配额信息
   */
  private _addStorageContext(): void {
    try {
      const miniappSdk = sdk();
      if (miniappSdk && typeof miniappSdk.getStorageInfoSync === 'function') {
        const info = miniappSdk.getStorageInfoSync();
        if (info) {
          const currentSize = info.currentSize || 0; // KB
          const limitSize = info.limitSize || 0; // KB
          const usagePercent = limitSize > 0 ? Math.round((currentSize / limitSize) * 100) : 0;

          const scope = getCurrentScope();
          scope.setContext('storage', {
            currentSize,
            limitSize,
            usagePercent,
          });

          if (usagePercent >= 80) {
            addBreadcrumb({
              category: 'storage.warning',
              message: `存储使用率 ${usagePercent}%（${currentSize}KB / ${limitSize}KB）`,
              level: 'warning',
              data: { currentSize, limitSize, usagePercent },
            });
          }
        }
      }
    } catch (_e) {
      // ignore
    }
  }

  /**
   * 检测小程序更新
   */
  private _checkAppUpdate(): void {
    try {
      const miniappSdk = sdk();
      if (miniappSdk && typeof miniappSdk.getUpdateManager === 'function') {
        const updateManager = miniappSdk.getUpdateManager();
        if (updateManager) {
          if (typeof updateManager.onCheckForUpdate === 'function') {
            updateManager.onCheckForUpdate((res: any) => {
              if (res && res.hasUpdate) {
                const scope = getCurrentScope();
                scope.setTag('has_update', 'true');

                addBreadcrumb({
                  category: 'app.update',
                  message: '检测到新版本可用',
                  level: 'info',
                  data: { hasUpdate: true },
                });
              }
            });
          }

          if (typeof updateManager.onUpdateReady === 'function') {
            updateManager.onUpdateReady(() => {
              addBreadcrumb({
                category: 'app.update',
                message: '新版本已下载完成',
                level: 'info',
                data: { updateReady: true },
              });
            });
          }
        }
      }
    } catch (_e) {
      // ignore
    }
  }
}
